import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import axios, { CancelToken } from "axios";
import { GetCustomer } from "api/rpc/2024-04/facilityAdmin/customer/customer";
import { StatusCode } from "api/protocols";
import Sheet from "components/sheet/Sheet";
import { Select } from "components/select/index";
import { ICustomer } from "redux/reducers/models/customer";
import GolferCard from "components/bookingPopUp/golferCard/GolferCard";
import Input from "components/form/input/Input";
// import { useTranslation } from "react-i18next";

interface IAddTournamentPlayerProps {
  open: boolean;
  onOk: (playerName: string, customer_id: number) => void;
  onCancel: () => void;
  organizer: ICustomer;
}

interface IPlayerState {
  name: string;
}

export default function AddTournamentPlayerModal(props: IAddTournamentPlayerProps) {
  const { t } = useTranslation();
  const [selectedCustomer, setSelectedCustomer] = useState<ICustomer>(null);
  const [customers, setCustomers] = useState<ICustomer[]>(undefined);
  const [customerQuery, setCustomerQuery] = useState<string>("");

  useEffect(() => {
    const source = axios.CancelToken.source();
    void loadCustomers(customerQuery, source.token);
    return () => source.cancel();
  }, [customerQuery]);

  const [playerState, setPlayerState] = useState<IPlayerState>({
    name: "",
  });

  async function loadCustomers(customerSearchQuery: string, cancelToken: CancelToken) {
    setCustomers(undefined);

    const customerRes = await GetCustomer({ search: customerSearchQuery }, false, cancelToken);

    if (customerRes.status !== StatusCode.OK) {
      return;
    }

    setCustomers(customerRes.data);
  }

  function onOk() {
    props.onOk(playerState.name, selectedCustomer ? selectedCustomer?.id : props.organizer?.id);
  }

  function handleCancelAddPlayer() {
    setPlayerState(prevState => ({ ...prevState, name: "" }));
    setSelectedCustomer(null);
    setCustomerQuery("");
    props.onCancel();
  }

  return (
    <Sheet
      title={t("Add Tournament Player")}
      open={props.open}
      size="small"
      closable
      onCancel={handleCancelAddPlayer}
      onOk={onOk}
      okDisabled={playerState.name === ""}
      okText={t("Add")}
      overflow
    >
      <div className="mt-2 mb-4">
        <Input
          id="name"
          value={playerState.name}
          onChange={e => setPlayerState(prev => ({ ...prev, name: e.target.value }))}
          label={t("Player Name")}
        />
      </div>

      {!props.organizer && (
        <>
          {selectedCustomer ? (
            <GolferCard
              closable
              removeGolfer={() => setSelectedCustomer(null)}
              email={selectedCustomer?.email}
              name={selectedCustomer?.full_name}
              memberCode={selectedCustomer?.member_code}
              customerType={selectedCustomer?.customer_type}
              phone={selectedCustomer?.phone}
            />
          ) : (
            <Select
              label={t("Organizer")}
              onChange={(value: string, extraValues: ICustomer) => setSelectedCustomer(extraValues)}
              showSearch
              searchValue={customerQuery}
              onSearch={(search: string) => setCustomerQuery(search)}
              searching={customers === undefined}
              placeholder={t("Search organizers...")}
              helpText={t("No tournament organizer set - please select a tournament organizer")}
            >
              {customers?.map(customer => (
                <Select.Option value={customer.id} key={customer.id} extraValues={customer}>
                  <div className="flex justify-between">
                    <div>
                      <div className="font-semibold text-lg">{customer?.full_name}</div>
                      <div className="text-sm text-gray-500">{customer.customer_type}</div>
                      <div className="text-sm text-gray-500">{customer.email}</div>
                      <div className="text-sm text-gray-500">{customer.phone ? customer.phone : null}</div>
                    </div>

                    <div className="font-medium text-base text-gray-500 self-end">{customer.member_code}</div>
                  </div>
                </Select.Option>
              ))}
            </Select>
          )}
        </>
      )}
    </Sheet>
  );
}
